import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Loader2, Play, Square, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, fmtTimeRange } from "@/lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export default function MyShifts() {
  const qc = useQueryClient();
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["my-shifts"],
    queryFn: () => api.get(`/enhancer/me/shifts?date_from=${plusDays(-30)}&date_to=${plusDays(60)}`).then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["my-shifts"] });

  const act = async (id, verb) => {
    try { const { data } = await api.post(`/enhancer/me/shifts/${id}/${verb}`); toast.success(verb === "clock-in" ? "Clocked in" : `Clocked out · ${data.actual_hours}h`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>;

  return (
    <div data-testid="enhancer-my-shifts">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarClock size={22} className="text-[#144F75]" /> My shifts</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Clock in when you start and out when you finish — your hours flow into payroll.</p>
      {shifts.length === 0 ? <p className="eyw-card p-6 text-sm text-[#4A5D6B]">No shifts assigned to you yet.</p> : (
        <div className="space-y-2">
          {shifts.map((s) => (
            <div key={s.id} data-testid={`my-shift-${s.id}`} className="eyw-card px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#1A242B]">{fmtDate(s.date)} · {fmtTimeRange(s.start_time, s.end_time)} {s.role && `· ${s.role}`}</p>
                <p className="text-xs text-[#4A5D6B] flex items-center gap-1"><MapPin size={11} /> {s.location_name}{s.actual_hours ? ` · ${s.actual_hours}h worked` : ""}</p>
              </div>
              <div>
                {s.clock_out ? <span className="text-xs px-2.5 py-1 rounded-full bg-[#5C8A72]/15 text-[#5C8A72] flex items-center gap-1"><CheckCircle2 size={12} /> Completed</span>
                  : s.clock_in ? <button onClick={() => act(s.id, "clock-out")} data-testid={`clock-out-${s.id}`} className="eyw-btn !py-1.5 text-xs !bg-[#B5794E]"><Square size={13} /> Clock out</button>
                  : <button onClick={() => act(s.id, "clock-in")} data-testid={`clock-in-${s.id}`} className="eyw-btn !py-1.5 text-xs"><Play size={13} /> Clock in</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
